import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, getCsrfCookie, onAuthEvent } from "../api";
import type { AuthUser } from "./types";

interface LoginCredentials {
  email: string;
  password: string;
}

interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

interface ResetPasswordPayload {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

/** App-level settings the SPA needs at boot — delivered by /me and /login. */
export interface ClientSettings {
  service_charge_enabled: boolean;
}

const DEFAULT_SETTINGS: ClientSettings = {
  service_charge_enabled: false,
};

/** Present only while an admin is viewing this account via "View as Tenant". */
export interface ImpersonationState {
  active: true;
  tenant_name: string | null;
  expires_at: string | null;
}

/** What the ["auth","me"] query cache holds when authenticated. */
interface AuthData {
  user: AuthUser;
  settings: ClientSettings;
  impersonation: ImpersonationState | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  settings: ClientSettings;
  /** Convenience: settings.service_charge_enabled */
  serviceChargeEnabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  impersonation: ImpersonationState | null;
  login: (credentials: LoginCredentials) => Promise<AuthUser>;
  logout: () => Promise<void>;
  stopImpersonating: () => Promise<void>;
  changePassword: (payload: ChangePasswordPayload) => Promise<AuthUser>;
  /** Pre-auth — accepts the same username-or-email identifier as login. */
  forgotPassword: (identifier: string) => Promise<string>;
  /** Pre-auth — token/email come from the reset link's query string. */
  resetPassword: (payload: ResetPasswordPayload) => Promise<string>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthData | null> {
  try {
    const { data } = await api.get<{
      user: AuthUser;
      settings?: ClientSettings;
      impersonation?: ImpersonationState | null;
    }>("/api/auth/me");
    return {
      user: data.user,
      settings: data.settings ?? DEFAULT_SETTINGS,
      impersonation: data.impersonation ?? null,
    };
  } catch {
    // 401 / 423 / network — treat as unauthenticated, let interceptor route
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: false,
  });

  // Listen for axios-emitted auth events and react in React-land
  useEffect(() => {
    const off = onAuthEvent((event) => {
      switch (event.type) {
        case "unauthenticated":
          queryClient.setQueryData(["auth", "me"], null);
          break;
        case "password_change_required":
          navigate("/force-password-change", { replace: true });
          break;
        case "session_expired":
          queryClient.setQueryData(["auth", "me"], null);
          toast.info("Your session expired due to inactivity. Please sign in again.");
          navigate("/login", { replace: true });
          break;
      }
    });
    return off;
  }, [queryClient, navigate]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<AuthData> => {
      await getCsrfCookie();
      const { data } = await api.post<{ user: AuthUser; settings?: ClientSettings }>(
        "/api/auth/login",
        credentials,
      );
      return { user: data.user, settings: data.settings ?? DEFAULT_SETTINGS, impersonation: null };
    },
    onSuccess: (authData) => {
      queryClient.setQueryData(["auth", "me"], authData);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await api.post("/api/auth/logout");
    },
    onSuccess: () => {
      queryClient.setQueryData(["auth", "me"], null);
      queryClient.clear();
    },
  });

  // Dedicated from logout: this is the banner's "Return to Admin" action.
  // It only clears the impersonation session keys server-side (see
  // ImpersonationManager::end()) — the admin's own session is untouched, so
  // there's nothing to clear from this SPA's query cache, just redirect.
  const stopImpersonatingMutation = useMutation({
    mutationFn: async () => {
      await api.post("/api/impersonation/stop");
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (payload: ChangePasswordPayload) => {
      const { data } = await api.post<{ user: AuthUser }>("/api/auth/change-password", payload);
      return data.user;
    },
    onSuccess: (user) => {
      // change-password response carries no settings — preserve what we have.
      queryClient.setQueryData<AuthData | null>(["auth", "me"], (prev) =>
        prev ? { ...prev, user } : { user, settings: DEFAULT_SETTINGS, impersonation: null },
      );
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async (identifier: string) => {
      const { data } = await api.post<{ message: string }>("/api/auth/forgot-password", { identifier });
      return data.message;
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (payload: ResetPasswordPayload) => {
      const { data } = await api.post<{ message: string }>("/api/auth/reset-password", payload);
      return data.message;
    },
  });

  const value = useMemo<AuthContextValue>(() => {
    const authData = meQuery.data ?? null;
    const user = authData?.user ?? null;
    const settings = authData?.settings ?? DEFAULT_SETTINGS;
    return {
      user,
      settings,
      serviceChargeEnabled: settings.service_charge_enabled,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!user,
      impersonation: authData?.impersonation ?? null,
      login: (creds) => loginMutation.mutateAsync(creds).then((d) => d.user),
      logout: () => logoutMutation.mutateAsync(),
      stopImpersonating: async () => {
        await stopImpersonatingMutation.mutateAsync();
        window.location.href = import.meta.env.VITE_ADMIN_APP_URL ?? "http://localhost:5173";
      },
      changePassword: (payload) => changePasswordMutation.mutateAsync(payload),
      forgotPassword: (identifier) => forgotPasswordMutation.mutateAsync(identifier),
      resetPassword: (payload) => resetPasswordMutation.mutateAsync(payload),
      hasPermission: (permission) => !!user && user.permissions.includes(permission),
      hasRole: (role) => !!user && user.roles.includes(role),
    };
  }, [
    meQuery.data,
    meQuery.isLoading,
    loginMutation,
    logoutMutation,
    stopImpersonatingMutation,
    changePasswordMutation,
    forgotPasswordMutation,
    resetPasswordMutation,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
